import { useFrame } from "@react-three/fiber";
import { useCallback, useMemo, useRef, useState } from "react";
import * as THREE from "three";

// Create a rough plastic bump texture (shared across all buttons)
function createRoughPlasticTexture(): THREE.DataTexture {
  const size = 128;
  const data = new Uint8Array(size * size);

  // Generate multi-octave noise for rough plastic look
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      let value = 0;

      // Multiple octaves of noise for varied roughness
      // Large bumps
      value += Math.random() * 0.3;
      // Medium detail
      value +=
        (Math.sin(x * 0.5 + Math.random() * 2) *
          Math.cos(y * 0.5 + Math.random() * 2) +
          1) *
        0.15;
      // Fine grain
      value += Math.random() * 0.2;

      // Normalize to 0-255
      data[y * size + x] = Math.floor(Math.min(255, Math.max(0, value * 255)));
    }
  }

  const texture = new THREE.DataTexture(data, size, size, THREE.RedFormat);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(4, 4);
  texture.needsUpdate = true;

  return texture;
}

// Shared texture instance
let sharedBumpTexture: THREE.DataTexture | null = null;

function useRoughPlasticTexture() {
  return useMemo(() => {
    if (!sharedBumpTexture) {
      sharedBumpTexture = createRoughPlasticTexture();
    }
    return sharedBumpTexture;
  }, []);
}

// Button types for the control panel
export type ControlButtonType =
  | "power"
  | "volumeUp"
  | "volumeDown"
  | "channelUp"
  | "channelDown"
  | "action"
  | "video";

export interface ControlPanelProps {
  /** Scale of the entire control panel (default: 1) */
  scale?: number;
  /** Position of the control panel */
  position?: [number, number, number];
  /** Rotation of the control panel */
  rotation?: [number, number, number];
  /** Whether the TV is powered on */
  isPoweredOn?: boolean;
  /** Callbacks for each button */
  onPowerPress?: () => void;
  onVolumeUpPress?: () => void;
  onVolumeDownPress?: () => void;
  onChannelUpPress?: () => void;
  onChannelDownPress?: () => void;
  onActionPress?: () => void;
  onVideoPress?: () => void;
}

interface RoundedButtonGeometryProps {
  radius: number;
  height: number;
  fillet: number;
  radialSegments?: number;
  curveSegments?: number;
}

function useRoundedButtonGeometry({
  radius,
  height,
  fillet,
  radialSegments = 32,
  curveSegments = 16,
}: RoundedButtonGeometryProps) {
  return useMemo(() => {
    const r = Math.max(0.001, Math.min(fillet, height * 0.49));
    const pts: THREE.Vector2[] = [];

    pts.push(new THREE.Vector2(radius, 0));
    pts.push(new THREE.Vector2(radius, height - r));

    const centerX = radius - r;
    const centerY = height - r;
    for (let i = 0; i <= curveSegments; i++) {
      const t = i / curveSegments;
      const a = (Math.PI / 2) * t;
      const x = centerX + r * Math.cos(a);
      const y = centerY + r * Math.sin(a);
      pts.push(new THREE.Vector2(x, y));
    }

    pts.push(new THREE.Vector2(0, height));

    const geo = new THREE.LatheGeometry(pts, radialSegments);
    geo.computeVertexNormals();
    return geo;
  }, [radius, height, fillet, radialSegments, curveSegments]);
}

interface ControlButtonProps {
  position: [number, number, number];
  rotation?: [number, number, number];
  radius: number;
  height: number;
  fillet: number;
  onPress?: () => void;
  label?: string;
}

function ControlButton({
  position,
  rotation = [0, 0, 0],
  radius,
  height,
  fillet,
  onPress,
}: ControlButtonProps) {
  const buttonRef = useRef<THREE.Group>(null);
  const [isPressed, setIsPressed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const pressProgress = useRef(0);

  const geometry = useRoundedButtonGeometry({ radius, height, fillet });

  useFrame((_, delta) => {
    if (!buttonRef.current) return;

    const targetPress = isPressed ? 1 : 0;
    const speed = 30;

    pressProgress.current = THREE.MathUtils.lerp(
      pressProgress.current,
      targetPress,
      delta * speed,
    );

    const pressDepth = height * 0.3;
    buttonRef.current.position.z = -pressProgress.current * pressDepth;
  });

  const handlePointerDown = useCallback(() => {
    setIsPressed(true);
  }, []);

  const handlePointerUp = useCallback(() => {
    setIsPressed(false);
    onPress?.();
  }, [onPress]);

  const handlePointerEnter = useCallback(() => {
    setIsHovered(true);
    document.body.style.cursor = "pointer";
  }, []);

  const handlePointerLeave = useCallback(() => {
    setIsHovered(false);
    setIsPressed(false);
    document.body.style.cursor = "auto";
  }, []);

  // Slightly lighter base colors + emissive glow for visibility in dark scenes
  const buttonColor = isHovered ? "#2a2a2a" : "#1a1a1a";
  const emissiveColor = isHovered ? "#4a4a4a" : "#3a3a3a";
  const emissiveIntensity = isPressed ? 0.25 : isHovered ? 0.6 : 0.4;
  const bumpTexture = useRoughPlasticTexture();

  return (
    <group position={position} rotation={rotation}>
      <group ref={buttonRef} rotation={[Math.PI * 0.5, 0, 0]}>
        <mesh
          geometry={geometry}
          castShadow
          receiveShadow
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
        >
          <meshStandardMaterial
            color={buttonColor}
            emissive={emissiveColor}
            emissiveIntensity={emissiveIntensity}
            roughness={0.85}
            metalness={0.02}
            bumpMap={bumpTexture}
            bumpScale={0.002}
          />
        </mesh>
      </group>
    </group>
  );
}

/**
 * Control Panel with Power, Volume, Channel, Action, and Video buttons
 * Arranged in a horizontal row with Power being the largest
 */
export function ControlPanel({
  scale = 1,
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  onPowerPress,
  onVolumeUpPress,
  onVolumeDownPress,
  onChannelUpPress,
  onChannelDownPress,
  onActionPress,
  onVideoPress,
}: ControlPanelProps) {
  // Button dimensions
  const powerRadius = 0.0065 * scale;
  const powerHeight = 0.008 * scale;
  const powerFillet = 0.001 * scale;

  const smallRadius = 0.004 * scale;
  const smallHeight = 0.005 * scale;
  const smallFillet = 0.0008 * scale;

  // Spacing between buttons
  const buttonSpacing = 0.0045 * scale;
  const medButtonSpacing = 0.0155 * scale;

  // Curve depth - how much the center bulges out vs edges
  const curveDepth = 0.003 * scale; // Adjust for more/less curve
  const powerY = 0;
  const smallY = -0.004; // Center-aligned with power button

  // Calculate X positions (linear row)
  const powerX = 0;
  let currentX = powerRadius + buttonSpacing;

  const volDownX = currentX + smallRadius;
  currentX += smallRadius * 2 + buttonSpacing;

  const volUpX = currentX + smallRadius;
  currentX += smallRadius * 2 + buttonSpacing;

  const chDownX = currentX + smallRadius;
  currentX += smallRadius * 2 + buttonSpacing;

  const chUpX = currentX + smallRadius;
  currentX += smallRadius * 2 + medButtonSpacing;

  const actionX = currentX + smallRadius;
  currentX += smallRadius * 2 + buttonSpacing;

  const videoX = currentX + smallRadius;

  // Total width for normalizing positions
  const totalWidth = videoX;

  // Helper to calculate z depth along curve (parabolic - edges recessed, center out)
  // normalizedX: 0 = left edge, 0.5 = center, 1 = right edge
  const getDepthZ = (x: number) => {
    const normalizedX = x / totalWidth; // 0 to 1
    const centeredX = normalizedX - 0.5; // -0.5 to 0.5
    // Parabola: z = depth * (1 - 4*x^2) gives max at center, 0 at edges
    const z = curveDepth * (1 - 4 * centeredX * centeredX);
    return z;
  };

  // Calculate transforms with depth curve
  const powerTransform = {
    position: [powerX, powerY, getDepthZ(powerX)] as [number, number, number],
    rotation: [0, 0, 0] as [number, number, number],
  };
  const volDownTransform = {
    position: [volDownX, smallY, getDepthZ(volDownX)] as [
      number,
      number,
      number,
    ],
    rotation: [0, 0, 0] as [number, number, number],
  };
  const volUpTransform = {
    position: [volUpX, smallY, getDepthZ(volUpX) - 0.004] as [
      number,
      number,
      number,
    ],
    rotation: [0, 0, 0] as [number, number, number],
  };
  const chDownTransform = {
    position: [chDownX, smallY, getDepthZ(chDownX) - 0.002] as [
      number,
      number,
      number,
    ],
    rotation: [0, 0, 0] as [number, number, number],
  };
  const chUpTransform = {
    position: [chUpX, smallY, getDepthZ(chUpX) - 0.003] as [
      number,
      number,
      number,
    ],
    rotation: [0, 0, 0] as [number, number, number],
  };
  const actionTransform = {
    position: [actionX, smallY, getDepthZ(actionX)] as [number, number, number],
    rotation: [0, 0, 0] as [number, number, number],
  };
  const videoTransform = {
    position: [videoX, smallY, getDepthZ(videoX)] as [number, number, number],
    rotation: [0, 0, 0] as [number, number, number],
  };

  return (
    <group position={position} rotation={rotation}>
      {/* Power Button - largest */}
      <ControlButton
        position={powerTransform.position}
        rotation={powerTransform.rotation}
        radius={powerRadius}
        height={powerHeight}
        fillet={powerFillet}
        onPress={onPowerPress}
        label="Power"
      />

      {/* Volume Down */}
      <ControlButton
        position={volDownTransform.position}
        rotation={volDownTransform.rotation}
        radius={smallRadius}
        height={smallHeight}
        fillet={smallFillet}
        onPress={onVolumeDownPress}
        label="Vol-"
      />

      {/* Volume Up */}
      <ControlButton
        position={volUpTransform.position}
        rotation={volUpTransform.rotation}
        radius={smallRadius}
        height={smallHeight}
        fillet={smallFillet}
        onPress={onVolumeUpPress}
        label="Vol+"
      />

      {/* Channel Down */}
      <ControlButton
        position={chDownTransform.position}
        rotation={chDownTransform.rotation}
        radius={smallRadius}
        height={smallHeight - 0.004}
        fillet={smallFillet}
        onPress={onChannelDownPress}
        label="Ch-"
      />

      {/* Channel Up */}
      <ControlButton
        position={chUpTransform.position}
        rotation={chUpTransform.rotation}
        radius={smallRadius}
        height={smallHeight - 0.002}
        fillet={smallFillet}
        onPress={onChannelUpPress}
        label="Ch+"
      />

      {/* Action */}
      <ControlButton
        position={actionTransform.position}
        rotation={actionTransform.rotation}
        radius={smallRadius}
        height={smallHeight + 0.005}
        fillet={smallFillet}
        onPress={onActionPress}
        label="Action"
      />

      {/* Video */}
      <ControlButton
        position={videoTransform.position}
        rotation={videoTransform.rotation}
        radius={smallRadius}
        height={smallHeight + 0.011}
        fillet={smallFillet}
        onPress={onVideoPress}
        label="Video"
      />
    </group>
  );
}
